
import { LogEntry, LogLevel } from './types';

/**
 * Base abstract logger class defining the core logging methods
 */
export abstract class BaseLogger {
  protected userId: string | null = null;

  /**
   * Set the current user ID for associating logs with users
   */
  public setUserId(userId: string | null): void {
    this.userId = userId;
  }

  /**
   * Log a message with specified level
   */
  public abstract log(
    level: LogLevel,
    message: string,
    details?: any,
    source?: string
  ): Promise<void>;

  /**
   * Helper methods for different log levels
   */
  public async debug(message: string, details?: any, source?: string): Promise<void> {
    return this.log('debug', message, details, source);
  }

  public async info(message: string, details?: any, source?: string): Promise<void> {
    return this.log('info', message, details, source);
  }

  public async warn(message: string, details?: any, source?: string): Promise<void> {
    return this.log('warn', message, details, source);
  }

  public async error(message: string, details?: any, source?: string): Promise<void> {
    return this.log('error', message, details, source);
  }

  public async fatal(message: string, details?: any, source?: string): Promise<void> {
    return this.log('fatal', message, details, source);
  }

  /**
   * Capture and log exceptions
   */
  public async captureException(
    error: Error | unknown,
    source?: string
  ): Promise<void> {
    const errorObj = error instanceof Error 
      ? { 
          name: error.name,
          message: error.message,
          stack: error.stack 
        } 
      : { message: String(error) };

    return this.error(
      errorObj.message || 'An unknown error occurred',
      errorObj,
      source
    );
  }
}
